// Default content shape for the public site.
// Admin can override any of these via /api/content (deep-merged on load).
// IMPORTANT: keep the *structure* (keys & array shapes) stable — pages read by key.

export const DEFAULT_CONTENT = {
  company: {
    name: "GESL Spinners Limited",
    former_name: "GESL Spinners Private Limited",
    cin: "U17299UP2022PLC170471",
    address_line1: "Gata No. 192 & 196, Village Temra,",
    address_line2: "Tehsil Bilaspur, Rampur,",
    address_line3: "Uttar Pradesh, India — 244921",
    phone: "+91 70601 01743",
    phone_raw: "+917060101743",
    email: "geslspinners@gmail.com",
    business_hours_label_weekday: "Monday – Saturday",
    business_hours_value_weekday: "09:00 – 18:00 IST",
    business_hours_label_sunday: "Sunday",
    business_hours_value_sunday: "Closed",
  },
  home: {
    hero_overline: "Spinning · Rampur · Uttar Pradesh",
    hero_line1: "Spun Yarns",
    hero_line2: "For The Next Era",
    hero_subtitle:
      "GESL Spinners Limited produces ring-spun, recycled polyester dope-dyed and speciality yarns from a modern facility in Rampur — built for global mills, brands and converters.",
    cta_primary_label: "Inquire Now",
    cta_secondary_label: "Explore Products",
    stats: [
      { value: "34,368", label: "Spindles Installed", sub: "Modern ring-spinning capacity" },
      { value: "6 – 50", label: "Count Range (Ne)", sub: "From coarse to fine yarns" },
      { value: "3-ply", label: "Multi-fold Capability", sub: "Single, two-ply & three-ply" },
    ],
    intro_overline: "A Spinning House",
    intro_heading_line1: "Material science",
    intro_heading_line2: "meets craft.",
    intro_paragraph:
      "GESL Spinners Limited (formerly known as GESL Spinners Private Limited) is a modern ring-spinning mill purpose-built for a more responsible textile chain. From recycled polyester dope-dyed yarns to BIO fibres, fire retardant blends, core-spun and fancy effect yarns — we partner with mills, brands and converters who care about how their yarn is made.",
    intro_link_text: "Read the company story",
    process_overline: "What We Spin",
    process_heading: "A complete yarn library.",
    process_cta: "See all products",
    process_cards: [
      { title: "Engineered Spinning", body: "34,368 spindles tuned for ring-spun and speciality constructions, with rigorous in-line QA." },
      { title: "Dope-Dyed RPSY", body: "Recycled polyester coloured at the polymer stage — saving water, energy and process emissions." },
      { title: "Wide Count Library", body: "From 6 Ne to 50 Ne, single to 3-ply, across BIO, FR, core-spun and fancy effect yarns." },
    ],
    rpsy_overline: "Signature · RPSY",
    rpsy_heading: "Recycled polyester, dyed at the source.",
    rpsy_paragraph:
      "Dope dyeing colours fibre at the polymer stage — eliminating the most water- and energy-intensive step in conventional polyester. Combined with post-consumer recycled feedstock, RPSY is one of the most thoughtful polyester yarns you can spec.",
    rpsy_button: "Explore RPSY",
    cta_overline: "Start a conversation",
    cta_heading: "Talk to our yarn team.",
    cta_paragraph: "Share your construction, count and quantity. We'll respond with samples and pricing.",
    cta_button: "Send an Inquiry",
  },
  about: {
    hero_overline: "About the Company",
    hero_line1: "A spinning unit",
    hero_line2: "built for responsible textiles.",
    hero_paragraph:
      "GESL Spinners Limited (formerly known as GESL Spinners Private Limited) operates a 34,368-spindle ring-spinning facility in Rampur, Uttar Pradesh. Our mandate is simple — engineer high-performing yarns across recycled polyester, speciality and natural fibres, while shrinking the environmental footprint of every kilogram we spin.",
    facts: [
      { key: "Legal Name", value: "GESL Spinners Limited", mono: false },
      { key: "CIN", value: "U17299UP2022PLC170471", mono: true },
      { key: "Located in", value: "Rampur, Uttar Pradesh", mono: false },
      { key: "Spindles", value: "34,368", mono: false },
    ],
    mission_overline: "Mission",
    mission_heading: "To spin yarns the world will be proud to wear.",
    mission_paragraph1:
      "Every brief is different — some need fine 50 Ne combed yarns, others a fire-retardant core for industrial workwear, and many ask for traceable, lower-impact polyester. We built GESL to serve all three with the same standard of consistency.",
    mission_paragraph2:
      "Our floor is process-driven: tight tolerances on count and twist, regular blend auditing, dope-dyed colour discipline, and a small, focused product family that we can keep promising on.",
    timeline_overline: "Trajectory",
    timeline_heading: "From private company to public unit.",
    timeline: [
      { year: "2022", title: "Incorporation", body: "Founded as a private limited company in Uttar Pradesh." },
      { year: "2023", title: "Capacity Build-out", body: "Installation of 34,368 modern ring-spinning spindles in Rampur." },
      { year: "2024", title: "RPSY Launch", body: "Commercial production of recycled polyester dope-dyed spun yarns." },
      { year: "2025", title: "Public Conversion", body: "Reconstituted as GESL Spinners Limited." },
    ],
    cta_heading: "Looking for a yarn partner you can rely on?",
    cta_button: "Talk to us",
  },
  products: {
    hero_overline: "Products",
    hero_line1: "The GESL",
    hero_line2: "yarn library.",
    hero_paragraph:
      "Seven core families, count ranges from 6 to 50 Ne, in single, two-ply and three-ply constructions. Each yarn is benchmarked against international quality norms and built around the requirements of weaving and knitting houses worldwide.",
    families_overline: "Yarn Families",
    families_heading: "Seven product families, one spinning floor.",
    yarn_types: [
      { name: "Ring Spun Yarn", short: "", desc: "Premium ring-spun yarns engineered for strength, evenness and superior fabric hand.", tag: "Core Product" },
      { name: "Recycled Polyester Dope Dyed Spun Yarn", short: "RPSY", desc: "Closed-loop colored fibre, dyed at the polymer stage — drastically lower water & energy footprint.", tag: "Signature" },
      { name: "BIO / R Yarns", short: "", desc: "Spun from organic / bio fibres and traceable recycled feedstock for responsible supply chains.", tag: "Sustainable" },
      { name: "Fire Retardant Yarns", short: "", desc: "Engineered for protective and technical end-uses with consistent FR performance.", tag: "Speciality" },
      { name: "Core Ring Spun Yarn", short: "", desc: "Filament-core architecture for stretch, durability and dimensional stability.", tag: "Speciality" },
      { name: "Fancy Yarns", short: "", desc: "Slubs, neps and effect yarns tailored to designer briefs and seasonal collections.", tag: "Speciality" },
      { name: "Multi-fold Yarns (up to 3-ply)", short: "", desc: "Plied constructions for weaving, knitting and technical applications.", tag: "Construction" },
    ],
    count_overline: "Count Range",
    count_paragraph: "A genuinely wide capability — coarse industrial counts on the same floor as fine, fashion-grade yarns. Multi-fold constructions up to 3-ply are available across most of the range.",
    count_grid: [
      { range: "Ne 6 – 10", note: "Coarse counts" },
      { range: "Ne 12 – 20", note: "Medium-coarse" },
      { range: "Ne 21 – 30", note: "Medium counts" },
      { range: "Ne 32 – 40", note: "Fine counts" },
      { range: "Ne 42 – 50", note: "Extra-fine counts" },
      { range: "Single · 2-ply · 3-ply", note: "Multi-fold constructions" },
    ],
    cta_heading: "Got a brief? Send it across.",
    cta_button: "Send an Inquiry",
  },
  contact: {
    hero_overline: "Contact",
    hero_heading: "Tell us about your yarn programme.",
    hero_subtitle: "Share construction, count and quantity. We'll respond with samples and a quote.",
    form_overline: "Inquiry Form",
    form_heading: "Start a conversation",
    form_disclaimer: "By submitting, you agree we may contact you about your inquiry.",
    business_hours_overline: "Business Hours",
    business_hours_heading: "When we're on the floor",
    business_hours_note: "For trade inquiries and samples, please use the form — our team typically responds within one business day.",
  },
};

// ---- Legacy named exports (kept as defaults for any non-refactored callers) ----
export const COMPANY = {
  name: DEFAULT_CONTENT.company.name,
  formerName: DEFAULT_CONTENT.company.former_name,
  cin: DEFAULT_CONTENT.company.cin,
  address: `${DEFAULT_CONTENT.company.address_line1} ${DEFAULT_CONTENT.company.address_line2} ${DEFAULT_CONTENT.company.address_line3}`,
  phone: DEFAULT_CONTENT.company.phone,
  phoneRaw: DEFAULT_CONTENT.company.phone_raw,
  email: DEFAULT_CONTENT.company.email,
};

export const STATS = DEFAULT_CONTENT.home.stats;
export const YARN_TYPES = DEFAULT_CONTENT.products.yarn_types;

export const DIRECTORS = [
  { name: "Vishnu Dutt Khandelwal", role: "Director" },
  { name: "Rajesh Sharma", role: "Director" },
  { name: "Sandeep Khandelwal", role: "Director" },
  { name: "Harsh Sharma", role: "Director" },
];

export const SECRETARY = { name: "Kavya Srivastava", role: "Company Secretary" };

export const IMAGES = {
  hero: "https://images.unsplash.com/photo-1636986056375-184676d8ca14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHx0ZXh0aWxlJTIweWFybiUyMHNwaW5uaW5nJTIwbWFjaGluZXxlbnwwfHx8fDE3ODEyNjM3ODZ8MA&ixlib=rb-4.1.0&q=85",
  products: "https://images.unsplash.com/photo-1660733101195-f688256c8854?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxjb3R0b24lMjB5YXJuJTIwc3Bvb2xzfGVufDB8fHx8MTc4MTI2Mzc4Nnww&ixlib=rb-4.1.0&q=85",
  sustainability: "https://images.unsplash.com/photo-1534639077088-d702bcf685e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGZhYnJpYyUyMHRleHR1cmV8ZW58MHx8fHwxNzgxMjYzNzg2fDA&ixlib=rb-4.1.0&q=85",
  infrastructure: "https://images.unsplash.com/photo-1660980041852-230420b8f99f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHx0ZXh0aWxlJTIweWFybiUyMHNwaW5uaW5nJTIwbWFjaGluZXxlbnwwfHx8fDE3ODEyNjM3ODZ8MA&ixlib=rb-4.1.0&q=85",
  about: "https://images.unsplash.com/photo-1588610992315-5654831ceebd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxzdXN0YWluYWJsZSUyMGZhYnJpYyUyMHRleHR1cmV8ZW58MHx8fHwxNzgxMjYzNzg2fDA&ixlib=rb-4.1.0&q=85",
};
